import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as taskService from "../../../../models/services/taskService";
import { SaveDataObj } from "source/framework/saveData";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListStateIconSuccess } from "source/components/framework/List/ListStateIconSuccess";
import { useEffect, useState } from "react";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { Box } from "@mui/material";
import { List } from "source/components/framework/List/List";
import { ITaskPriorityListItem, TaskPriorityListItem } from "source/models/taskPriority/taskPriorityListItem";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { TaskPriorityDialog } from "../../TaskSettingsDialog/TaskPriorityList/TaskPriorityDialog";
import { TaskPriorityEdit } from "source/models/taskPriority/taskPriorityEdit";
import { TaskPriorityEditItem } from "source/models/taskPriority/taskPriorityEditItem";

interface ITaskTypeDialogTaskPriorityListProp {
    companyId: string;
    taskTypeId: string;
    taskPriorities: ITaskPriorityListItem[];
    onChange: (taskStates: ITaskPriorityListItem[]) => void;
}

interface ITaskTypeDialogTaskPriorityListState {
    items: ITaskPriorityListItem[];
    isReadOnly: boolean;
    selectedId: string;
    isLoading: boolean;
    showEditDialog: boolean;
    editDialogId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

export const TaskTypeDialogTaskPriorityList = (props: ITaskTypeDialogTaskPriorityListProp) => {
    const [state, setState] = useState<ITaskTypeDialogTaskPriorityListState>({
        items: props.taskPriorities,
        isReadOnly: props.taskPriorities.length < 1,
        isLoading: false,
        selectedId: null,
        showEditDialog: false,
        editDialogId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false,
    });

    const searchItems = async (moveNext: boolean = false, setEditDialogData: boolean = true) => {
        setState(old => ({
            ...old,
            isLoading: true
        }));
        const result = await AppUtils.callService(() => taskService.getTaskPriorityListItems(props.companyId, props.taskTypeId, 10000, 1, null, "activeState", false), false, false,
            () => setState(old => ({
                ...old,
                isLoading: false
            })));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, [], result.items, true, false);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        setState(old => ({
            ...old,
            items: items.items,
            isReadOnly: result.items.length < 1,
            selectedId,
            editDialogId: setEditDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || result.hasMore) : state.editDialogHasNext,
        }));
        //To cache latest result to form some default values do not flash when switching between pages in task type
        props.onChange(items.items);
    };

    const refreshList = async (setEditDialogData: boolean = true) => {
        await searchItems(false, setEditDialogData);
    };

    const isNewTaskType = props.taskTypeId === Base.emptyGuid;

    useEffect(() => {
        if (!props.taskTypeId || isNewTaskType) return;
        searchItems();
    }, [props.taskTypeId]);

    useEffect(() => {
        if (!props.taskTypeId || !isNewTaskType) return;
        setState(old => ({
            ...old,
            items: props.taskPriorities,
            selectedId: props.taskPriorities.find(i => i.id === old.selectedId)?.id || null,
            isReadOnly: props.taskPriorities.length < 1,
        }));
    }, [props.taskPriorities]);

    const handleSearchLines = (_sortColumn: string, _sortOrderIsAsc: boolean, nextPage: boolean) => {
        if (isNewTaskType) return;
        searchItems(!nextPage, false);
    };

    const handleSetSelectedIds = (selectedId: string, _checkedIds: string[]) => {
        setState(old => ({
            ...old,
            selectedId,
        }));
    };

    const handleAdd = () => {
        setState(old => ({
            ...old,
            showEditDialog: true,
            editDialogId: Base.emptyGuid,
            editDialogHasPrevious: false,
            editDialogHasNext: false
        }));
    };

    const handleOpenLine = (id: string) => {
        const index = state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        setState(old => ({
            ...old,
            showEditDialog: true,
            editDialogId: id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < state.items.length - 1,
            selectedId: id
        }));
    };

    const handleSaved = (item: ITaskPriorityListItem) => {
        if (isNewTaskType) {
            const taskPriorities = [...props.taskPriorities.filter(i => i.id !== item.id), item];
            props.onChange(TaskPriorityListItem.sortTaskPriorityListItems(taskPriorities));
        } else {
            refreshList(false);
        }
    };

    const handleEditDialogCancel = (doRefreshList: boolean) => {
        setState(old => ({
            ...old,
            showEditDialog: false
        }));
        if (!doRefreshList || isNewTaskType) return;
        refreshList(false);
    };

    const getRemoveSaveData = (props: ITaskTypeDialogTaskPriorityListProp, ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("taskTypeId", props.taskTypeId);
        result.append("ids", ids);
        return result;
    };

    const remove = async (ids: string[]): Promise<ApiSuccess> => {
        if (ids.length < 1) return;
        if (!isNewTaskType) {
            const saveData = getRemoveSaveData(props, ids);
            const result = await AppUtils.callService(() => taskService.removeTaskPriorities(saveData.obj));
            if (!result) return null;
            refreshList();
        } else {
            props.onChange(props.taskPriorities.filter(i => ids.indexOf(i.id) < 0));
        }
        return new ApiSuccess();
    };

    const handleRemove = async () => {
        const selectedIds = List.getSelectedIds(state.selectedId, []);
        const selectedItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1);
        if (selectedItems.length < 1) return;
        if (await AppUtils.showConfirmationDialog(selectedItems.length > 1
            ? Translations.DoYouReallyWantToRemoveSelectedTaskPriorities
            : String.format(Translations.DoYouReallyWantToRemoveTaskPriorityParameter, selectedItems[0].code), false) === ConfirmationDialogResult.Yes) {
            remove(selectedIds);
        }
    };

    const setEditDialogItemByIndex = (index: number) => {
        if (index < 0) return;
        setState(old => ({
            ...old,
            selectedId: state.items[index].id,
            editDialogId: state.items[index].id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < state.items.length - 1
        }));
    };

    return (
        <>
            <DialogSectionCard
                headerTitle={Translations.TaskPriorities}
                buttons={
                    <ListIconButtonsItem
                        addTooltip={Translations.AddTaskPriority}
                        removeTooltip={Translations.RemoveTaskPriority}
                        removeDisabled={!state.selectedId}
                        onAdd={handleAdd}
                        onRemove={!state.isReadOnly ? handleRemove : null}
                    />
                }
            >
                <Box sx={{ margin: "-1rem", height: "calc(100% + 40px)" }}>
                    <List
                        intialized={true}
                        isLoading={state.isLoading}
                        hasMore={false}
                        globalLoadingInUse={true}
                        noDataMessage={Translations.TaskDefaultPrioritiesInUse}
                        height="100%"
                        lines={state.items.map((item) => {
                            return {
                                columns: [
                                    { size: 10, content: <ListLinkValue onClick={() => handleOpenLine(item.id)}>{item.code}</ListLinkValue> },
                                    { size: 2, content: item.activeState < 1 ? null : <ListStateIconSuccess tooltip={Translations.InUse} /> }
                                ],
                                id: item.id,
                                classes: item.activeState < 1 ? "disabled" : ""
                            };
                        })}
                        sortColumn={""}
                        sortOrderIsAsc={true}
                        selectedId={state.selectedId}
                        checkedIds={[]}
                        onSetSelectedIds={handleSetSelectedIds}
                        onSearchLines={handleSearchLines}
                        onOpenLine={handleOpenLine}
                    />
                </Box>
            </DialogSectionCard>
            {(state.showEditDialog && !!state.editDialogId) &&
                <TaskPriorityDialog
                    isReadOnly={state.isReadOnly && state.editDialogId !== Base.emptyGuid}
                    id={state.editDialogId}
                    companyId={props.companyId}
                    taskTypeId={props.taskTypeId}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onSaved={handleSaved}
                    onClose={handleEditDialogCancel}
                    onRemove={remove}
                    onRefreshList={async () => {
                        if (isNewTaskType) return;
                        await refreshList(false);
                    }}
                    onPrevious={() => List.moveToPrevious(state, setEditDialogItemByIndex)}
                    onNext={() => List.moveToNext(state, setEditDialogItemByIndex)}
                    onGetTaskPriorityEdit={isNewTaskType
                        ? (_companyId: string, id: string) => {
                            let item = new TaskPriorityListItem();
                            if (id === Base.emptyGuid) {
                                item.id = Base.emptyGuid;
                                item.activeState = 1;
                            } else {
                                item = props.taskPriorities.find(i => i.id === id);
                                if (!item) return Base.getPromiseResult(null);
                            }
                            const result = new TaskPriorityEdit();
                            result.item = new TaskPriorityEditItem(item);
                            return Base.getPromiseResult(result);
                        }
                        : undefined
                    }
                />
            }
        </>
    );
};